import { Link, useEntry, useNavigate } from "@/lib/page-rendering"
import { forwardRef, useEffect } from "react"

import { searchOriginOf, type SearchOrigin } from "./search-pages"

export type SearchReturnIndex =
  | "classes"
  | "educators"
  | "relief_listings"
  | "shortlists"

const SEARCH_RETURN_INDEXES: readonly string[] = [
  "classes",
  "educators",
  "relief_listings",
  "shortlists",
] satisfies SearchReturnIndex[]

// A search trail keeps at most this many hops after its type crumb; older
// ones fold behind a "…".
export const MAX_SEARCH_HOPS = 3

const RETURN_PARAMETER = "search_return"
const STORAGE_PREFIX = "sagecool.search-return."
const MAX_RECORDS = 20
const RECORD_LIFETIME_MS = 30 * 60 * 1000

// A record page the user passed through on the way to the current one: its
// page (an Inertia component name in the search registry), its URL with
// that page's own return key, and the record's name.
export type SearchHop = { page: string; href: string; name: string }

// How the user reached a search page: the results they left (index and
// target), then the record pages followed since, oldest first. folded says
// older hops were dropped to keep the cap.
export type SearchReturn = {
  index: SearchReturnIndex
  target: string
  hops: SearchHop[]
  folded: boolean
}

type ReturnRecord = SearchReturn & { expiresAt: number }

function isHop(hop: unknown): hop is SearchHop {
  if (typeof hop !== "object" || hop === null) return false
  const candidate = hop as Partial<SearchHop>
  return (
    typeof candidate.page === "string" &&
    typeof candidate.href === "string" &&
    isAllowedTarget(candidate.href) &&
    typeof candidate.name === "string"
  )
}

function isReturnRecord(record: unknown): record is ReturnRecord {
  if (typeof record !== "object" || record === null) return false
  const candidate = record as Partial<ReturnRecord>
  return (
    typeof candidate.index === "string" &&
    SEARCH_RETURN_INDEXES.includes(candidate.index) &&
    typeof candidate.target === "string" &&
    Array.isArray(candidate.hops) &&
    candidate.hops.length <= MAX_SEARCH_HOPS &&
    candidate.hops.every(isHop) &&
    typeof candidate.folded === "boolean" &&
    typeof candidate.expiresAt === "number" &&
    Number.isFinite(candidate.expiresAt)
  )
}

// The plain results page of an index, for a page reached with no record.
export function defaultSearchTarget(index: SearchReturnIndex) {
  return `/search/${index}`
}

function isAllowedTarget(target: string) {
  if (!target.startsWith("/") || target.startsWith("//")) return false

  const url = new URL(target, window.location.origin)
  return (
    url.origin === window.location.origin &&
    (url.pathname === "/search" || url.pathname.startsWith("/search/"))
  )
}

function storageKey(key: string) {
  return `${STORAGE_PREFIX}${key}`
}

function createKey() {
  const bytes = new Uint8Array(18)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  )
}

function pruneStorage(now = Date.now()) {
  const records = Object.keys(sessionStorage)
    .filter((key) => key.startsWith(STORAGE_PREFIX))
    .map((key) => {
      const value = sessionStorage.getItem(key)
      try {
        const record = value === null ? null : JSON.parse(value)
        return { key, expiresAt: isReturnRecord(record) ? record.expiresAt : 0 }
      } catch {
        return { key, expiresAt: 0 }
      }
    })
    .sort((left, right) => left.expiresAt - right.expiresAt)

  records.forEach((record) => {
    if (record.expiresAt <= now) sessionStorage.removeItem(record.key)
  })

  const activeRecords = records.filter((record) => record.expiresAt > now)
  activeRecords
    .slice(0, Math.max(0, activeRecords.length - (MAX_RECORDS - 1)))
    .forEach((record) => {
      sessionStorage.removeItem(record.key)
    })
}

export function currentSearchReturnUrl() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

export function saveSearchReturn(searchReturn: SearchReturn, now = Date.now()) {
  if (
    !isAllowedTarget(searchReturn.target) ||
    searchReturn.hops.length > MAX_SEARCH_HOPS ||
    !searchReturn.hops.every(isHop)
  )
    throw new Error("Invalid search return target")

  pruneStorage(now)
  const key = createKey()
  const record: ReturnRecord = {
    ...searchReturn,
    expiresAt: now + RECORD_LIFETIME_MS,
  }
  sessionStorage.setItem(storageKey(key), JSON.stringify(record))
  return key
}

export function returnUrlFor(destination: string, searchReturn: SearchReturn) {
  const key = saveSearchReturn(searchReturn)
  const url = new URL(destination, window.location.origin)
  url.searchParams.set(RETURN_PARAMETER, key)
  return `${url.pathname}${url.search}${url.hash}`
}

// The current page's return record, or null when it was reached directly,
// through a shared link, or after its record expired or was tampered with.
export function resolveSearchReturn(now = Date.now()): SearchReturn | null {
  const key = new URLSearchParams(window.location.search).get(RETURN_PARAMETER)
  if (key === null || !/^[a-f0-9]{36}$/.test(key)) return null

  const keyName = storageKey(key)
  const value = sessionStorage.getItem(keyName)
  try {
    const record = value === null ? null : JSON.parse(value)
    if (
      !isReturnRecord(record) ||
      record.expiresAt <= now ||
      !isAllowedTarget(record.target)
    ) {
      sessionStorage.removeItem(keyName)
      return null
    }
    const { index, target, hops, folded } = record
    return { index, target, hops, folded }
  } catch {
    sessionStorage.removeItem(keyName)
    return null
  }
}

// The record a search link followed from the current page saves for the
// next one. From results it starts a trail at those results. From a record
// page it extends the current trail, or the page's canonical one when it has
// none, with the page itself as the newest hop, dropping the oldest past the
// cap. Any other page hands its trail on unchanged.
export function nextSearchReturn(
  origin: SearchOrigin,
  current: SearchReturn | null,
  url: string,
): SearchReturn {
  if (origin.kind === "results") {
    return { index: origin.index, target: url, hops: [], folded: false }
  }

  const base = current ?? {
    index: origin.index,
    target: defaultSearchTarget(origin.index),
    hops: [],
    folded: false,
  }
  if (origin.kind === "page") return base

  const hops = [
    ...base.hops,
    { page: origin.page, href: url, name: origin.name },
  ]
  return {
    index: base.index,
    target: base.target,
    hops: hops.slice(-MAX_SEARCH_HOPS),
    folded: base.folded || hops.length > MAX_SEARCH_HOPS,
  }
}

// The name of the record the current page shows, which a search link
// followed from it records as its hop. SearchPageChrome keeps it, from the
// identity it is given; one page is current at a time.
let currentRecord: { page: string; name: string } | null = null

export function useCurrentSearchRecord(page: string, name?: string) {
  useEffect(() => {
    if (name === undefined) return
    const record = { page, name }
    currentRecord = record
    return () => {
      if (currentRecord === record) currentRecord = null
    }
  }, [page, name])
}

function currentRecordName(page: string) {
  if (currentRecord === null || currentRecord.page !== page) {
    throw new Error(`Search page ${page} names no record for its hop`)
  }
  return currentRecord.name
}

function isSameTabClick(event: React.MouseEvent) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}

type SearchLinkProps = {
  href: string
  children: React.ReactNode
  className?: string
  role?: string
  "aria-label"?: string
  onClick?: (event: React.MouseEvent) => void
}

// A link from one search page to another. Followed in the same tab, it saves
// the trail so far (the results left and the record pages passed through)
// for the next page's crumbs; opened in a new tab it is a plain link, whose
// page shows its canonical trail.
export const SearchDetailLink = forwardRef<HTMLAnchorElement, SearchLinkProps>(
  function SearchDetailLink({ href, children, ...props }, ref) {
    const page = useEntry().name
    const navigate = useNavigate()

    return (
      <Link
        ref={ref}
        {...props}
        href={href}
        onClick={(event) => {
          props.onClick?.(event)
          if (event.defaultPrevented || !isSameTabClick(event)) return

          event.preventDefault()
          const origin = searchOriginOf(page, currentRecordName)
          navigate({
            href: returnUrlFor(
              href,
              nextSearchReturn(
                origin,
                resolveSearchReturn(),
                currentSearchReturnUrl(),
              ),
            ),
          })
        }}
      >
        {children}
      </Link>
    )
  },
)
